/* UMAI pixel field — ported from src/components/system/PixelField.tsx.
 *
 * A grid of tiny squares, most sitting near-black, brightness biased toward a
 * focal point, with individual cells easing to new brightnesses so the field
 * twinkles — and the rare cell popping toward white like a star catching light.
 *
 * One canvas, one rAF loop, throttled to ~24fps: the motion is slow easing, so
 * more frames buy nothing. Cells retarget on their own clocks (every 1.5-6s
 * each), which is what makes it read as a living field rather than a GIF loop.
 *
 * ── THE ONE DEPARTURE FROM THE REFERENCE ──────────────────────────────────
 * The reference starts the field from an IntersectionObserver. That is right
 * for the animation and WRONG for the field itself: an observer that never
 * fires — hidden tab, a browser that throttles it, the harness this theme gets
 * verified in — would leave the footer with no field at all. This site has
 * twice shipped content that was invisible because an observer never
 * delivered, and the rule that came out of it is that nothing is gated behind
 * one.
 *
 * So the field is DRAWN ONCE, immediately, at its resting brightness. The
 * observer only decides whether it also twinkles. Worst case is a still field,
 * which is exactly what was there before this file existed.
 *
 * And with no JavaScript at all the canvas keeps the CSS dot-grid background
 * that assets/umai-system.css paints on it; this clears that only once it has
 * something of its own to show.
 */
(function () {
  'use strict';

  function build(canvas) {
    if (canvas.__pxInit) return;
    canvas.__pxInit = true;

    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    var d = canvas.dataset;
    var focalX = parseFloat(d.focalX);   if (isNaN(focalX)) focalX = 0.62;
    var focalY = parseFloat(d.focalY);   if (isNaN(focalY)) focalY = 0.58;
    var pitch  = parseFloat(d.pitch)  || 11;
    var SIZE   = parseFloat(d.size)   || 2;
    var gain   = parseFloat(d.gain)   || 1;
    var vertical = d.falloff === 'vertical';
    var ink = d.ink || '#e8e6df';

    var reduceQ = window.matchMedia('(prefers-reduced-motion: reduce)');
    var reduce = reduceQ.matches;

    /* `gain` runs the whole field hotter. It has to lift THREE things at once
       or it does nothing visible: the ceiling, the near-black floor, and the
       bias of the random draw — the power below is what parks most cells at
       the bottom of their range, so raising only the ceiling leaves the field
       looking exactly as dim as before. */
    var G = Math.max(0.2, gain);
    var BIAS = 3 / G;

    var cells = [], W = 0, H = 0, raf = 0;

    function ease(t) { return t * (2 - t); }

    function retarget(c, now) {
      c.from = c.a;
      /* Mostly a dim wander; a few percent of retargets pop toward white —
         the star glint. */
      c.to = Math.random() < Math.min(0.12, 0.03 * G)
        ? Math.min(1, c.ceil + 0.45)
        : Math.pow(Math.random(), BIAS) * c.ceil;
      c.t0 = now;
      c.t1 = now + 600 + Math.random() * 900;
      c.next = c.t1 + 1500 + Math.random() * 4500;
    }

    function draw(now, animate) {
      ctx.clearRect(0, 0, W, H);
      /* Hoisted: every cell is painted the same colour, so setting it per cell
         was a few thousand redundant assignments a frame on a wide footer. */
      ctx.fillStyle = ink;
      for (var i = 0; i < cells.length; i++) {
        var c = cells[i];
        if (animate) {
          if (now >= c.next) retarget(c, now);
          if (now < c.t1) c.a = c.from + (c.to - c.from) * ease(Math.min(1, (now - c.t0) / (c.t1 - c.t0)));
          else c.a = c.to;
        }
        if (c.a < 0.02) continue;
        ctx.globalAlpha = c.a;
        ctx.fillRect(c.x, c.y, SIZE, SIZE);
      }
      ctx.globalAlpha = 1;
    }

    /* Sized from the LIVE box and rebuilt whenever it changes: a canvas laid
       out in a hidden tab measures 0x0, and a field initialised at zero width
       has zero cells forever. The observer makes mount-time size irrelevant. */
    function init() {
      var nextW = canvas.clientWidth, nextH = canvas.clientHeight;
      if (!nextW || !nextH) return;
      /* ResizeObserver fires once on observe, and again on every URL-bar
         show/hide while scrolling a phone. A same-size call is a no-op. */
      if (nextW === W && nextH === H && cells.length) return;

      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = nextW; H = nextH;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      var fx = W * focalX, fy = H * focalY;
      /* `falloff` decides what "far from the focal point" means.
           radial   — distance in both axes; reads as a glow.
           vertical — distance in Y only, so brightness is UNIFORM across the
                      width. On a band this wide a radial falloff puts every
                      star in the middle and leaves the outer thirds empty. */
      var maxD = vertical
        ? (Math.max(fy, H - fy) || 1)
        : Math.sqrt(Math.pow(Math.max(fx, W - fx), 2) + Math.pow(Math.max(fy, H - fy), 2));

      cells = [];
      for (var x = pitch / 2; x < W; x += pitch) {
        for (var y = pitch / 2; y < H; y += pitch) {
          var dist = (vertical ? Math.abs(y - fy) : Math.sqrt((x - fx) * (x - fx) + (y - fy) * (y - fy))) / maxD;
          /* The brightness ceiling falls off from the focal point; the floor is
             near-black, so most of the field only whispers. */
          var ceil = Math.min(1, Math.max(0.06 * G, 0.55 * G * Math.pow(1 - dist, 2)));
          cells.push({
            x: x, y: y, ceil: ceil,
            a: Math.pow(Math.random(), BIAS) * ceil,
            from: 0, to: 0, t0: 0, t1: 0,
            /* FIRST retarget, not the steady-state one. The reference spreads
               this over 3s, which means a field that has just scrolled into
               view sits still for a beat before anything moves — and the beat
               is exactly when someone is looking at it. 500ms still staggers
               the cells enough that they never move as a block; the 1.5-6s
               spacing below takes over from the second retarget on. */
            next: Math.random() * 500
          });
        }
      }
      /* Resting state, painted NOW — see the note at the top of this file. */
      draw(0, false);
      canvas.style.backgroundImage = 'none';
    }

    /* BOTH, deliberately. ResizeObserver is the right signal — it catches a
       footer that grows because its own content reflowed, which `resize` never
       sees. But a canvas whose backing store is stale after a viewport change
       does not degrade quietly, it STRETCHES: a 750px field blown across
       1440px of footer. `init()` early-returns on an unchanged size, so the
       second listener costs nothing and removes that failure mode entirely. */
    if ('ResizeObserver' in window) new ResizeObserver(init).observe(canvas);
    window.addEventListener('resize', init);
    /* Orientation flips on a phone land before the new size is laid out. */
    window.addEventListener('orientationchange', function () { window.setTimeout(init, 200); });
    init();

    var last = 0, running = false;
    function loop(now) {
      raf = window.requestAnimationFrame(loop);
      if (now - last < 40) return;   /* ~24fps is plenty for slow easing */
      last = now;
      draw(now, true);
    }
    function start() {
      if (running || reduce || !cells.length) return;
      running = true; raf = window.requestAnimationFrame(loop);
    }
    function stop() {
      if (!running) return;
      running = false; window.cancelAnimationFrame(raf);
    }

    /* Reduced motion is honoured live: the field is already drawn, so turning
       the setting on simply stops it moving rather than blanking it. */
    var onPref = function () { reduce = reduceQ.matches; if (reduce) stop(); else start(); };
    if (reduceQ.addEventListener) reduceQ.addEventListener('change', onPref);
    else if (reduceQ.addListener) reduceQ.addListener(onPref);

    /* The footer is several viewports below the fold on a product page, so the
       loop must not run from load onwards animating something nobody can see.
       `rootMargin` starts it well before the edge appears — 600px rather than
       the reference's 250 — so by the time the footer is actually on screen
       the cells are already mid-ease instead of starting from rest under the
       reader's eye. If this never fires, the still field above is what shows. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries[entries.length - 1].isIntersecting ? start() : stop();
      }, { rootMargin: '600px 0px' }).observe(canvas);
    } else {
      start();
    }

    /* rAF is parked in a background tab; re-check on the way back rather than
       waiting for a scroll that may never come. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
    });
  }

  function boot() {
    var nodes = document.querySelectorAll('canvas[data-pixel-field]');
    for (var i = 0; i < nodes.length; i++) build(nodes[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
